import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Database, Download, Loader2, Pencil, RefreshCw, Search, Trash2, Triangle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;

function downloadBlob(data, filename, type) {
  const blob = new Blob([data], { type });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function DevAdminDB() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState("");
  const [collQuery, setCollQuery] = useState("");

  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [dateFields, setDateFields] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [dateField, setDateField] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const [editDoc, setEditDoc] = useState(null); // { id, json }
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // row id
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const loadCollections = useCallback(async () => {
    try {
      const { data } = await api.get("/devadmin/db/collections");
      setCollections(data.collections || []);
      if (!active && data.collections?.length) setActive(data.collections[0].name);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load collections");
    }
  }, [active]);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const filterParams = useMemo(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (dateField && (start || end)) {
      p.date_field = dateField;
      if (start) p.start = start;
      if (end) p.end = end;
    }
    return p;
  }, [q, dateField, start, end]);

  const loadDocs = useCallback(async (pg = page) => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/devadmin/db/${active}`, {
        params: { page: pg, page_size: PAGE_SIZE, ...filterParams },
      });
      setRows(data.rows || []);
      setFields(data.fields || []);
      setDateFields(data.date_fields || []);
      setTotal(data.total || 0);
      setPage(data.page || pg);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load records");
      setRows([]); setFields([]); setTotal(0);
    } finally { setLoading(false); }
  }, [active, filterParams, page]);

  // Reset paging + filters when switching collection.
  useEffect(() => {
    setPage(1); setQ(""); setDateField(""); setStart(""); setEnd("");
  }, [active]);

  useEffect(() => { if (active) loadDocs(1); /* eslint-disable-next-line */ }, [active]);

  const applyFilters = () => loadDocs(1);

  const idOf = (row) => row.id || row._id;

  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`/devadmin/db/${active}/doc/${idOf(row)}`);
      setEditDoc(row);
      setEditText(JSON.stringify(data.doc, null, 2));
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not open record");
    }
  };

  const saveEdit = async () => {
    let parsed;
    try { parsed = JSON.parse(editText); }
    catch { toast.error("Invalid JSON — fix the syntax first"); return; }
    setSaving(true);
    try {
      await api.patch(`/devadmin/db/${active}/doc/${idOf(editDoc)}`, { doc: parsed });
      toast.success("Record updated");
      setEditDoc(null);
      loadDocs();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/devadmin/db/${active}/doc/${deleteTarget}`);
      toast.success("Record deleted");
      setDeleteTarget(null);
      loadDocs();
      loadCollections();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const exportData = async (fmt) => {
    try {
      const r = await api.get(`/devadmin/db/${active}/export`, {
        params: { fmt, ...filterParams },
        responseType: "blob",
      });
      downloadBlob(r.data, `${active}.${fmt}`, fmt === "json" ? "application/json" : "text/csv");
    } catch {
      toast.error("Export failed");
    }
  };

  const bulkWord = useMemo(() => `DELETE ${total}`, [total]);
  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/devadmin/db/${active}/bulk-delete`, {
        confirm_count: total,
        confirm_all: !filterParams.q && !filterParams.date_field,
        ...filterParams,
      });
      toast.success(`Deleted ${data.deleted} record${data.deleted === 1 ? "" : "s"}`);
      setBulkOpen(false); setBulkConfirm("");
      loadDocs(1); loadCollections();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const visibleCols = useMemo(() => fields.slice(0, 8), [fields]);
  const filteredCollections = useMemo(() => {
    const s = collQuery.trim().toLowerCase();
    return s ? collections.filter((c) => c.name.toLowerCase().includes(s)) : collections;
  }, [collections, collQuery]);

  return (
    <Layout>
      <div className="space-y-5 max-w-[1400px]" data-testid="devadmin-db-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Database className="h-7 w-7 text-fuchsia-500" /> Database Explorer
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Browse, edit and delete any record across the live database. Handle with care.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          {/* Collections rail */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 h-fit" data-testid="db-collections">
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input value={collQuery} onChange={(e) => setCollQuery(e.target.value)} placeholder="Find collection…" className="pl-8 h-9 rounded-xl text-sm" data-testid="db-collection-search" />
            </div>
            <div className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filteredCollections.map((c) => (
                <button
                  key={c.name}
                  onClick={() => setActive(c.name)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm transition-colors ${active === c.name ? "bg-fuchsia-600 text-white" : "hover:bg-slate-100 dark:hover:bg-slate-700/60 text-slate-700 dark:text-slate-200"}`}
                  data-testid={`db-collection-${c.name}`}
                >
                  <span className="truncate font-medium">{c.name}</span>
                  <span className={`text-xs shrink-0 ${active === c.name ? "text-fuchsia-100" : "text-slate-400"}`}>{c.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Records panel */}
          <div className="space-y-3 min-w-0">
            {/* Toolbar */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilters()} placeholder="Search text fields…" className="pl-8 h-9 rounded-xl text-sm" data-testid="db-search" />
                </div>
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => loadDocs()} disabled={loading} data-testid="db-refresh-btn">
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportData("json")} data-testid="db-export-json">
                  <Download className="h-3.5 w-3.5 mr-1" /> JSON
                </Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportData("csv")} data-testid="db-export-csv">
                  <Download className="h-3.5 w-3.5 mr-1" /> CSV
                </Button>
                <Button size="sm" className="rounded-full bg-rose-500 hover:bg-rose-600 text-white" onClick={() => { setBulkConfirm(""); setBulkOpen(true); }} disabled={total === 0} data-testid="db-bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete {total}
                </Button>
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Date field</div>
                  <Select value={dateField || "none"} onValueChange={(v) => setDateField(v === "none" ? "" : v)}>
                    <SelectTrigger className="h-9 rounded-xl text-sm w-44" data-testid="db-datefield-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {dateFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">From</div>
                  <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 rounded-xl text-sm" disabled={!dateField} data-testid="db-start" />
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">To</div>
                  <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 rounded-xl text-sm" disabled={!dateField} data-testid="db-end" />
                </div>
                <Button size="sm" className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700" onClick={applyFilters} data-testid="db-apply-filters">Apply</Button>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="db-table">
                  <thead className="bg-slate-50 dark:bg-slate-900/40 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      {visibleCols.map((f) => <th key={f} className="text-left px-3 py-2.5 whitespace-nowrap">{f}</th>)}
                      <th className="text-right px-3 py-2.5">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {rows.length === 0 && !loading && (
                      <tr><td colSpan={visibleCols.length + 1} className="text-center py-12 text-slate-400" data-testid="db-empty">No records.</td></tr>
                    )}
                    {rows.map((row) => (
                      <tr key={idOf(row)} className="hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`db-row-${idOf(row)}`}>
                        {visibleCols.map((f) => (
                          <td key={f} className="px-3 py-2.5 max-w-[260px] truncate text-slate-700 dark:text-slate-200" title={String(row[f] ?? "")}>
                            {row[f] === undefined || row[f] === null ? <span className="text-slate-300">—</span> : String(row[f])}
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" className="rounded-full text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/30" onClick={() => openEdit(row)} data-testid={`db-edit-${idOf(row)}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" onClick={() => setDeleteTarget(idOf(row))} data-testid={`db-delete-${idOf(row)}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 dark:border-slate-700 text-sm text-slate-500">
                <span data-testid="db-total">{total} record{total === 1 ? "" : "s"}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1 || loading} onClick={() => loadDocs(page - 1)} data-testid="db-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <span data-testid="db-page">{page} / {totalPages}</span>
                  <Button variant="outline" size="sm" className="rounded-full" disabled={page >= totalPages || loading} onClick={() => loadDocs(page + 1)} data-testid="db-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit dialog */}
        <Dialog open={!!editDoc} onOpenChange={(o) => { if (!o) setEditDoc(null); }}>
          <DialogContent className="max-w-2xl" data-testid="db-edit-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }}>Edit record</DialogTitle>
              <DialogDescription>{active} · {editDoc && idOf(editDoc)} — edit the raw JSON. The <code>_id</code> field is ignored on save.</DialogDescription>
            </DialogHeader>
            <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={18} className="font-mono text-xs" data-testid="db-edit-textarea" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDoc(null)} disabled={saving}>Cancel</Button>
              <Button onClick={saveEdit} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="db-edit-save">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Single delete confirm */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
          <DialogContent className="max-w-md" data-testid="db-delete-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2 text-rose-600">
                <Trash2 className="h-5 w-5" /> Delete record?
              </DialogTitle>
              <DialogDescription>This permanently removes <span className="font-mono">{deleteTarget}</span> from <span className="font-semibold">{active}</span>. This cannot be undone.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button className="bg-rose-500 hover:bg-rose-600" onClick={confirmDelete} data-testid="db-delete-confirm">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk delete confirm (typed) */}
        <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkConfirm(""); } }}>
          <DialogContent className="max-w-md" data-testid="db-bulk-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2 text-rose-600">
                <Triangle className="h-5 w-5 fill-rose-100" /> Bulk delete {total} record{total === 1 ? "" : "s"}
              </DialogTitle>
              <DialogDescription>
                This deletes <span className="font-semibold">{total}</span> record{total === 1 ? "" : "s"} from <span className="font-semibold">{active}</span> matching the current filters
                {(!filterParams.q && !filterParams.date_field) ? " (the ENTIRE collection — no filter applied)" : ""}. This cannot be undone.
                <br />Type <span className="font-mono font-bold text-rose-600">{bulkWord}</span> to confirm.
              </DialogDescription>
            </DialogHeader>
            <Input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)} placeholder={bulkWord} className="font-mono" data-testid="db-bulk-confirm-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkConfirm(""); }} disabled={bulkBusy}>Cancel</Button>
              <Button className="bg-rose-500 hover:bg-rose-600" disabled={bulkConfirm !== bulkWord || bulkBusy} onClick={doBulkDelete} data-testid="db-bulk-confirm-btn">
                {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />} Delete {total}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
